import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import * as _ from 'lodash';
import axiosInstance from '../../axiosInstance';
import { createResetAction } from '../../utils/index';
import { fetchSubscriptionStatusCountsService } from '../services/user/api';
import { SubscriptionStatusCountItem } from '../services/user/types';
import { setShowAlert } from '../utilts/utilsSlice';
import { sendSPUDataRefreshStatusEmail } from '../services/dashboard/api';
import { StoreMethods } from '../store';
import { SendSPUDataRefreshEmailPayloadType } from './interfaces/index';
import { TOAST_TYPE } from './../../utils/constantTypes';
import { ACTION_NAMES, SLICES } from '../../constants';
import { AccessToken } from '../user/interfaces';

interface DashboardState {
  loading: boolean;
  error: null | string;
  data: [];
  totalAppointments: number;
  totalReports: number;
  report: [];
  reportsStatus: {
    start: number;
    paused: number;
    complete: number;
    submitted: number;
  };
  totalSubscriptions: number;
  subscriptionsStatus: {
    active: SubscriptionStatusCountItem;
    paid: SubscriptionStatusCountItem;
    expired: SubscriptionStatusCountItem;
    cancelled: SubscriptionStatusCountItem;
  };
  practices: [];
  currentPractice: null;
}

type FetchReportPayloadType = {
  accessToken: string;
  filters: {
    startDate: Date;
    endDate: Date;
  };
  sikkaToken: string;
};

const initialState: DashboardState = {
  loading: false,
  error: null,
  data: [],
  totalAppointments: 0,
  totalReports: 0,
  report: [],
  reportsStatus: {
    start: 0,
    paused: 0,
    complete: 0,
    submitted: 0
  },
  totalSubscriptions: 0,
  subscriptionsStatus: {
    active: {
      mainUserCount: 0,
      assistantUserCount: 0,
      totalCount: 0
    },
    paid: {
      mainUserCount: 0,
      assistantUserCount: 0,
      totalCount: 0
    },
    expired: {
      mainUserCount: 0,
      assistantUserCount: 0,
      totalCount: 0
    },
    cancelled: {
      mainUserCount: 0,
      assistantUserCount: 0,
      totalCount: 0
    }
  },
  practices: [],
  currentPractice: null
};

const dashboardSlice = createSlice({
  name: 'dashboard',
  initialState,
  reducers: {
    setCurrentPractice: (state, action) => {
      state.currentPractice = action.payload.officeId;
    },
    resetDashboard: createResetAction(initialState)
  },
  extraReducers: (builder) => {
    builder.addCase(fetchAppointmentsAsync.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(fetchAppointmentsAsync.fulfilled, (state, action) => {
      state.loading = false;
      state.totalAppointments = action.payload.totalCount;
    });
    builder.addCase(fetchAppointmentsAsync.rejected, (state, action) => {
      state.loading = false;
    });
    builder.addCase(fetchReportsAsync.pending, (state, action) => {
      state.loading = true;
    });
    builder.addCase(fetchReportsAsync.fulfilled, (state, action) => {
      state.loading = false;
      state.totalAppointments = action.payload.totalCount;
      // state.totalReports = action.payload.totalCount;
      state.reportsStatus = action.payload.reportStatuses;
      state.report = action.payload.reportData;
    });
    builder.addCase(fetchReportsAsync.rejected, (state, action) => {
      state.loading = false;
    });
    builder.addCase(fetchSubscriptionStatsAsync.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchSubscriptionStatsAsync.fulfilled, (state, action) => {
      state.loading = false;
      state.error = null;
      state.totalSubscriptions = action.payload.totalSubscriptions;
      state.subscriptionsStatus = action.payload.subscriptions;
    });
    builder.addCase(fetchSubscriptionStatsAsync.rejected, (state, action) => {
      state.loading = false;
      state.error = _.get(action, 'payload.msg', null);
    });
    builder.addCase(fetchAllPracticesAsync.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(fetchAllPracticesAsync.fulfilled, (state, action) => {
      state.loading = false;
      state.practices = action.payload?.practiceData;
    });
    builder.addCase(fetchAllPracticesAsync.rejected, (state, action) => {
      state.loading = false;
    });
    builder.addCase(syncCalendarEventsAsync.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(syncCalendarEventsAsync.fulfilled, (state, action) => {
      state.loading = false;
      state.report = action.payload.reportData;
    });
    builder.addCase(syncCalendarEventsAsync.rejected, (state) => {
      state.loading = false;
    });
    builder.addCase(sendSPUDataRefreshStatusEmailAsync.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(
      sendSPUDataRefreshStatusEmailAsync.fulfilled,
      (state, action) => {
        state.loading = false;
      }
    );
    builder.addCase(sendSPUDataRefreshStatusEmailAsync.rejected, (state) => {
      state.loading = false;
    });
  }
});
function getFormattedDate(payload) {
  let date = new Date();
  if (payload) {
    date = new Date(payload);
  }
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');

  const formattedDate = `${year}-${month}-${day}`;
  return formattedDate;
}

const fetchAppointments = async (payload) => {
  const accessToken = _.get(payload, 'accessToken');
  const sikkaToken = _.get(payload, 'sikkaToken');
  const startDate = _.get(payload, 'startDate');
  const endDate = _.get(payload, 'endDate');
  const startdate = `${getFormattedDate(startDate)}T00:00:00`;
  const enddate = `${getFormattedDate(endDate)}T00:00:00`;
  const appointmentsRes = await axiosInstance.get('sikka/appointments', {
    params: {
      startDate: startdate,
      endDate: enddate
    },
    headers: {
      Authorization: `Bearer ${accessToken}`,
      ['Sikka-Auth']: sikkaToken
    }
  });
  const appointmentsData = _.get(appointmentsRes, 'data.data', []);
  return appointmentsData;
};

export const fetchAppointmentsAsync = createAsyncThunk(
  'dashboard/fetchAppointments',
  async (payload: {
    accessToken: string;
    sikkaToken: string;
    startDate: Date;
    endDate: Date;
  }) => {
    const appointmentsData = await fetchAppointments(payload);
    return { totalCount: _.get(appointmentsData, 'total_count', 0) };
  }
);

export const fetchReportsAsync = createAsyncThunk(
  'dashboard/fetchReportsAsync',
  async (payload: FetchReportPayloadType, { dispatch }) => {
    try {
      const accessToken = _.get(payload, 'accessToken', '');
      const sikkaToken = _.get(payload, 'sikkaToken', '');
      const startDate = _.get(payload, 'filters.startDate', '');
      const endDate = _.get(payload, 'filters.endDate', '');

      const appointmentsData = await fetchAppointments({
        accessToken,
        sikkaToken,
        startDate,
        endDate
      });
      const reportRes = await axiosInstance.get('report/all', {
        params: {
          ...payload.filters
        },
        headers: {
          Authorization: `Bearer ${payload.accessToken}`
        }
      });
      const reportData = _.get(reportRes, 'data.data.data');
      const reportStatuses = _.reduce(
        reportData,
        (acc, curr) => {
          const status = curr.status;

          if (acc[status] !== undefined) {
            acc[status]++;
          } else {
            acc[status] = 1;
          }

          return acc;
        },
        {
          start: 0,
          paused: 0,
          complete: 0,
          submitted: 0
        }
      );
      return {
        totalCount: _.get(appointmentsData, 'total_count', 0),
        reportStatuses,
        reportData
      };
    } catch (err) {
      dispatch(
        setShowAlert({
          show: true,
          message: err?.response?.data?.msg || err?.response?.data?.message,
          type: 'error'
        })
      );
    }
  }
);

export const fetchSubscriptionStatsAsync = createAsyncThunk(
  'dashboard/fetchSubscriptionStats',
  async (payload: AccessToken, thunkApi) => {
    try {
      const response = await fetchSubscriptionStatusCountsService(
        payload,
        thunkApi
      );
      const subscriptionCounts = _.get(
        response,
        'data',
        initialState.subscriptionsStatus
      );
      const activeCount = _.get(subscriptionCounts, 'active.totalCount', 0);
      const expiredCount = _.get(subscriptionCounts, 'expired.totalCount', 0);
      const cancelledCount = _.get(
        subscriptionCounts,
        'cancelled.totalCount',
        0
      );

      return {
        totalSubscriptions: activeCount + expiredCount + cancelledCount,
        subscriptions: subscriptionCounts
      };
    } catch (err) {
      return thunkApi.rejectWithValue(err?.response?.data);
    }
  }
);
export const fetchAllPracticesAsync = createAsyncThunk(
  'dashboard/fetchAllPractices',
  async (payload: { accessToken: string }, { dispatch }) => {
    try {
      const accessToken = _.get(payload, 'accessToken', '');
      const practicesRes = await axiosInstance.get('practice/all', {
        headers: {
          Authorization: `Bearer ${accessToken}`
        }
      });
      const practiceData = _.get(practicesRes, 'data.data', []);
      return { practiceData };
    } catch (err) {
      dispatch(
        setShowAlert({
          show: true,
          message: err?.response?.data?.msg || err?.response?.data?.message,
          type: 'error'
        })
      );
    }
  }
);

export const syncCalendarEventsAsync = createAsyncThunk(
  'dashboard/syncCalendar',
  async (payload: FetchReportPayloadType, { dispatch }) => {
    try {
      const accessToken = _.get(payload, 'accessToken', '');
      const sikkaToken = _.get(payload, 'sikkaToken', '');
      const startDate = _.get(payload, 'filters.startDate', '');
      const endDate = _.get(payload, 'filters.endDate', '');
      const appointmentsData = await fetchAppointments({
        accessToken,
        sikkaToken,
        startDate,
        endDate
      });
      const reportRes = await axiosInstance.get('report/all', {
        params: {
          ...payload.filters
        },
        headers: {
          Authorization: `Bearer ${payload.accessToken}`
        }
      });
      const reportData = _.get(reportRes, 'data.data.data');
      const syncedReportData = _.map(appointmentsData.items, (obj) => {
        const year = new Date(obj.date).getFullYear();
        const month = new Date(obj.date).getMonth();
        const day = new Date(obj.date).getDate();
        const [hours, minutes] = obj.time.split(':');
        const startTime = new Date(
          year,
          month,
          day,
          parseInt(hours),
          parseInt(minutes)
        );
        const endTime = new Date(startTime.getTime() + obj.length * 60000);
        return {
          start: startTime.toISOString(),
          end: endTime.toISOString(),
          title: _.get(obj, 'patient_name'),
          resourceId: _.get(obj, 'operatory', 'OP-1'),
          report_status: _.get(
            _.find(reportData, { appointmentId: obj.appointment_sr_no }),
            'report_status',
            'start'
          ),
          appointmentId: obj.appointment_sr_no
        };
      });
      return { reportData: syncedReportData };
    } catch (err) {
      dispatch(
        setShowAlert({
          show: true,
          message: err?.response?.data?.msg || err?.response?.data?.message,
          type: 'error'
        })
      );
    }
  }
);

export const sendSPUDataRefreshStatusEmailAsync = createAsyncThunk<
  void,
  SendSPUDataRefreshEmailPayloadType,
  StoreMethods
>(
  `${SLICES.DASHBOARD}/${ACTION_NAMES.SEND_SPU_DATA_REFRESH_STATUS_EMAIL}`,
  async (payload, { dispatch, rejectWithValue }) => {
    try {
      const res = await sendSPUDataRefreshStatusEmail(payload, {
        dispatch
      });

      const statusCode = _.get(res, 'statusCode', null);
      if (statusCode === 200) {
        dispatch(
          setShowAlert({
            show: true,
            message: _.get(res, 'msg'),
            type: TOAST_TYPE.SUCCESS
          })
        );
      }
    } catch (err) {
      console.log('Error', err);
      return rejectWithValue(err?.message);
    }
  }
);

export const { setCurrentPractice, resetDashboard } = dashboardSlice.actions;
export default dashboardSlice.reducer;
